/**
 * Muse Moi - Custom JavaScript
 *
 * Main custom JavaScript file for Muse Moi theme customizations.
 * This file loads after base theme scripts.
 *
 * Structure:
 * 1. Utility Functions
 * 2. DOM Helpers
 * 3. Custom Components
 * 4. Event Handlers
 * 5. Initialization
 */

(function() {
  'use strict';

  /**
   * Main namespace for Muse Moi custom functionality
   */
  const MuseMoi = {

    // ============================================
    // 1. UTILITY FUNCTIONS
    // ============================================

    /**
     * Debounce function to limit how often a function can run
     * @param {Function} func - Function to debounce
     * @param {number} wait - Wait time in milliseconds
     * @returns {Function} Debounced function
     */
    debounce(func, wait = 300) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    },

    /**
     * Throttle function to limit execution frequency
     * @param {Function} func - Function to throttle
     * @param {number} limit - Time limit in milliseconds
     * @returns {Function} Throttled function
     */
    throttle(func, limit = 300) {
      let inThrottle;
      return function(...args) {
        if (!inThrottle) {
          func.apply(this, args);
          inThrottle = true;
          setTimeout(() => inThrottle = false, limit);
        }
      };
    },

    /**
     * Check if element is in viewport
     * @param {HTMLElement} element - Element to check
     * @returns {boolean} True if element is in viewport
     */
    isInViewport(element) {
      const rect = element.getBoundingClientRect();
      return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
      );
    },

    /**
     * Get breakpoint value from CSS variable
     * @param {string} breakpoint - Breakpoint name (sm, md, lg, xl, 2xl)
     * @returns {number} Breakpoint value in pixels
     */
    getBreakpoint(breakpoint) {
      const value = getComputedStyle(document.documentElement)
        .getPropertyValue(`--mm-breakpoint-${breakpoint}`);
      return parseInt(value);
    },

    /**
     * Check current viewport size against breakpoint
     * @param {string} breakpoint - Breakpoint name
     * @returns {boolean} True if viewport is >= breakpoint
     */
    isBreakpoint(breakpoint) {
      return window.innerWidth >= this.getBreakpoint(breakpoint);
    },


    // ============================================
    // 2. DOM HELPERS
    // ============================================

    /**
     * Query selector helper
     * @param {string} selector - CSS selector
     * @param {HTMLElement} context - Context element (default: document)
     * @returns {HTMLElement|null} Selected element
     */
    $(selector, context = document) {
      return context.querySelector(selector);
    },

    /**
     * Query selector all helper
     * @param {string} selector - CSS selector
     * @param {HTMLElement} context - Context element (default: document)
     * @returns {NodeList} Selected elements
     */
    $$(selector, context = document) {
      return context.querySelectorAll(selector);
    },

    /**
     * Add event listener with optional delegation
     * @param {string|HTMLElement} target - Element or selector
     * @param {string} event - Event type
     * @param {Function} handler - Event handler
     * @param {string} delegateSelector - Optional delegate selector
     */
    on(target, event, handler, delegateSelector = null) {
      const element = typeof target === 'string' ? this.$(target) : target;
      if (!element) return;

      if (delegateSelector) {
        element.addEventListener(event, (e) => {
          if (e.target.matches(delegateSelector) || e.target.closest(delegateSelector)) {
            handler.call(e.target.closest(delegateSelector), e);
          }
        });
      } else {
        element.addEventListener(event, handler);
      }
    },


    // ============================================
    // 3. CUSTOM COMPONENTS
    // ============================================

    /**
     * Example: Custom accordion component
     * Uncomment and customize as needed
     */
    /*
    initAccordion() {
      const accordions = this.$$('.mm-accordion');

      accordions.forEach(accordion => {
        const trigger = this.$('.mm-accordion__trigger', accordion);
        const content = this.$('.mm-accordion__content', accordion);

        if (trigger && content) {
          trigger.addEventListener('click', () => {
            const isOpen = accordion.classList.contains('is-open');
            accordion.classList.toggle('is-open', !isOpen);
            content.style.maxHeight = isOpen ? '0' : `${content.scrollHeight}px`;
          });
        }
      });
    },
    */

    /**
     * Example: Custom tabs component
     * Uncomment and customize as needed
     */
    /*
    initTabs() {
      const tabGroups = this.$$('.mm-tabs');

      tabGroups.forEach(group => {
        const tabs = this.$$('.mm-tab', group);
        const panels = this.$$('.mm-tab-panel', group);

        tabs.forEach((tab, index) => {
          tab.addEventListener('click', () => {
            // Remove active states
            tabs.forEach(t => t.classList.remove('is-active'));
            panels.forEach(p => p.classList.remove('is-active'));

            // Add active states
            tab.classList.add('is-active');
            panels[index].classList.add('is-active');
          });
        });
      });
    },
    */


    // ============================================
    // 4. EVENT HANDLERS
    // ============================================

    /**
     * Handle scroll events
     */
    handleScroll() {
      // Add custom scroll behavior here
      // Example: Add class to body when scrolled
      const scrolled = window.scrollY > 100;
      document.body.classList.toggle('is-scrolled', scrolled);
    },

    /**
     * Handle resize events
     */
    handleResize() {
      // Add custom resize behavior here
      // Example: Update layout based on viewport size
    },


    // ============================================
    // 5. INITIALIZATION
    // ============================================

    /**
     * Initialize all custom functionality
     */
    init() {
      // Initialize components
      // Uncomment as you add components:
      // this.initAccordion();
      // this.initTabs();

      // Set up event listeners
      window.addEventListener('scroll', this.throttle(() => this.handleScroll(), 100));
      window.addEventListener('resize', this.debounce(() => this.handleResize(), 300));

      // Trigger initial scroll handler
      this.handleScroll();

      // Add initialization complete class
      document.documentElement.classList.add('mm-loaded');
    }
  };


  // ============================================
  // AUTO-INITIALIZE
  // ============================================

  /**
   * Initialize when DOM is ready
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => MuseMoi.init());
  } else {
    MuseMoi.init();
  }

  // Expose MuseMoi to global scope for external access if needed
  window.MuseMoi = MuseMoi;

})();


/**
 * DEVELOPMENT NOTES
 *
 * - Keep this file focused on global functionality and small components
 * - Create separate JS files for complex components (mm-component-slider.js, etc.)
 * - Use the MuseMoi namespace to avoid global scope pollution
 * - Document complex functions and browser-specific workarounds
 * - Consider using modern ES6+ features (supported in Shopify themes)
 * - Test across browsers (especially Safari for iOS)
 *
 * TODO:
 * - Add custom components as needed
 * - Document any dependencies on base theme JavaScript
 * - Add feature detection for progressive enhancement
 */
